import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Archive, Check, Download, FileText, Link2, Loader2, Pencil, Trash2, Unlink, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";
import { MismatchPrompt } from "@/components/business/MismatchPrompt";

export const openFile = async (url) => {
  try { const { data } = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank", "noopener"); }
  catch (err) { toast.error(formatApiError(err)); }
};

const Dropzone = ({ cid, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const send = async (list) => {
    const files = [...(list || [])];
    if (!files.length) return;
    if (files.length > 20) return toast.error("20 fichiers maximum par envoi");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post(`/business/companies/${cid}/invoices/batch`, fd);
      toast.success(`${data.accepted} facture(s) en cours de lecture`);
      data.rejected.forEach((r) => toast.error(`${r.name} : ${r.error}`));
      onDone();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); send(e.dataTransfer.files); }}
      className={cn("flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed p-8 text-center transition-colors", over ? "border-primary bg-primary/5" : "border-border")} data-testid="invoice-dropzone">
      {busy ? <Loader2 className="h-8 w-8 animate-spin text-primary" /> : <UploadCloud className="h-8 w-8 text-primary" />}
      <p className="font-bold">Déposez vos factures ici</p>
      <p className="text-xs text-muted-foreground">PDF ou photos · jusqu'à 20 fichiers · 10 Mo max chacun. L'IA lit le fournisseur, le numéro, la date et les montants.</p>
      <input ref={ref} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => send(e.target.files)} data-testid="invoice-input" />
      <Button size="sm" className="rounded-full" disabled={busy} onClick={() => ref.current?.click()} data-testid="invoice-pick">Choisir des fichiers</Button>
    </div>
  );
};

const Edit = ({ inv, base, onChange, onClose }) => {
  const ex = inv.extracted || {};
  const [v, setV] = useState({ supplier_name: ex.supplier_name || "", invoice_number: ex.invoice_number || "", date: ex.date || "", total_ttc: ex.total_ttc ?? "" });
  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.entries({ ...v, total_ttc: v.total_ttc === "" ? null : Number(v.total_ttc) }).filter(([, x]) => x !== "" && x != null));
    try { onChange((await api.put(base, body)).data); onClose(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className="grid grid-cols-2 gap-2" data-testid={`invoice-edit-${inv.id}`}>
      <Input value={v.supplier_name} onChange={(e) => setV({ ...v, supplier_name: e.target.value })} placeholder="Fournisseur" className="col-span-2" data-testid="invoice-edit-supplier" />
      <Input value={v.invoice_number} onChange={(e) => setV({ ...v, invoice_number: e.target.value })} placeholder="N° facture" data-testid="invoice-edit-number" />
      <Input type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} data-testid="invoice-edit-date" />
      <Input type="number" step="0.01" min="0.01" value={v.total_ttc} onChange={(e) => setV({ ...v, total_ttc: e.target.value })} placeholder="Total TTC €" data-testid="invoice-edit-total" />
      <Button type="submit" size="sm" className="rounded-full" data-testid="invoice-edit-save">Enregistrer</Button>
    </form>
  );
};

const InvoiceRow = ({ inv, cid, company, onChange }) => {
  const { money, date } = useFormat();
  const [edit, setEdit] = useState(false);
  const base = `/business/companies/${cid}/invoices/${inv.id}`;
  const ex = inv.extracted || {};
  const act = async (fn, msg) => { try { const r = await fn(); onChange(r.data); if (msg) toast.success(msg); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <li className="card-surface space-y-3 p-4" data-testid={`invoice-${inv.id}`}>
      <div className="flex flex-wrap items-start gap-2">
        <button type="button" onClick={() => openFile(`${base}/file`)} className="tone-chip tone-sky flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" aria-label="Voir la facture" data-testid={`invoice-file-${inv.id}`}><FileText className="h-5 w-5" /></button>
        <div className="min-w-0 flex-1">
          <div className="truncate font-bold">{ex.supplier_name || inv.file_name}{inv.source === "email" && <span className="ml-2 rounded-full bg-sky/50 px-2 py-0.5 align-middle text-[10px] font-semibold" data-testid={`invoice-email-${inv.id}`}>reçue par email</span>}{inv.card_last4 && <span className="ml-1 rounded-full bg-muted px-2 py-0.5 align-middle text-[10px] font-semibold">•••• {inv.card_last4}</span>}</div>
          <div className="text-xs text-muted-foreground">{inv.status === "processing" ? "Lecture en cours…" : [ex.invoice_number && `n° ${ex.invoice_number}`, ex.date && date(ex.date), ex.siren && `SIREN ${ex.siren}`].filter(Boolean).join(" · ") || inv.file_name}</div>
        </div>
        {inv.status === "processing" ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <div className="num text-lg font-extrabold">{ex.total_ttc != null ? money(ex.total_ttc, { maximumFractionDigits: 2 }) : "—"}</div>}
      </div>
      {inv.status === "error" && <p className="flex items-center gap-1.5 text-xs font-semibold text-destructive"><AlertTriangle className="h-3.5 w-3.5" />{inv.error}</p>}
      {inv.mismatch && <MismatchPrompt company={company} type="invoice" item={inv} />}
      {inv.duplicate_of && <p className="flex items-center gap-1.5 text-xs font-semibold text-destructive" data-testid={`invoice-duplicate-${inv.id}`}><AlertTriangle className="h-3.5 w-3.5" />Doublon probable : cette facture a déjà été déposée.</p>}
      {inv.match ? (
        <div className="flex flex-wrap items-center gap-2 rounded-xl bg-success/10 p-2.5 text-xs" data-testid={`invoice-matched-${inv.id}`}>
          <Check className="h-4 w-4 text-success" /><span className="flex-1">Rapprochée : {inv.match_tx ? `${date(inv.match_tx.date)} · ${inv.match_tx.label} · ${money(Math.abs(inv.match_tx.amount), { maximumFractionDigits: 2 })}` : "opération"}</span>
          <button type="button" onClick={() => act(() => api.delete(`${base}/match`))} className="flex items-center gap-1 underline" data-testid={`invoice-unmatch-${inv.id}`}><Unlink className="h-3 w-3" />Annuler</button>
        </div>
      ) : inv.status === "extracted" && (
        inv.suggestions.length ? <div className="space-y-1.5" data-testid={`invoice-suggestions-${inv.id}`}>
          <p className="text-xs font-bold">Opération correspondante ? À valider</p>
          {inv.suggestions.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border p-2 text-xs">
              <Badge variant="secondary" className="rounded-full">{t.score} %</Badge>
              <span className="min-w-0 flex-1 truncate">{date(t.date)} · {t.label}</span><b className="num">{money(Math.abs(t.amount), { maximumFractionDigits: 2 })}</b>
              <Button size="sm" className="h-7 rounded-full text-xs" onClick={() => act(() => api.post(`${base}/match`, { tx_id: t.id }), "Rapprochement validé")} data-testid={`invoice-match-${inv.id}-${t.id}`}><Link2 className="mr-1 h-3 w-3" />Valider</Button>
            </div>))}
        </div> : <p className="text-xs text-muted-foreground" data-testid={`invoice-nomatch-${inv.id}`}>Aucune opération du même montant trouvée pour l'instant.</p>
      )}
      {edit && <Edit inv={inv} base={base} onChange={onChange} onClose={() => setEdit(false)} />}
      {inv.status !== "processing" && <div className="flex gap-2">
        <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs" onClick={() => setEdit(!edit)} data-testid={`invoice-edit-toggle-${inv.id}`}><Pencil className="mr-1 h-3 w-3" />Corriger</Button>
        <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs text-destructive" onClick={() => act(() => api.delete(base), "Facture supprimée")} data-testid={`invoice-delete-${inv.id}`}><Trash2 className="mr-1 h-3 w-3" />Supprimer</Button>
      </div>}
    </li>
  );
};

const FILTERS = [["todo", "À valider"], ["matched", "Rapprochées"], ["all", "Toutes"]];

const Export = ({ cid }) => {
  const prev = new Date(); prev.setDate(1); prev.setMonth(prev.getMonth() - 1);
  const [month, setMonth] = useState(prev.toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/business/companies/${cid}/invoices/export`, { params: { month }, responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || `factures_${month}.zip`;
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: name });
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Archive téléchargée");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface flex flex-wrap items-center gap-2 p-4" data-testid="invoice-export">
      <Archive className="h-5 w-5 shrink-0 text-primary" /><div className="min-w-0 flex-1 basis-48"><div className="text-sm font-bold">Archive pour le cabinet comptable</div>
        <div className="text-[11px] text-muted-foreground">ZIP classé par fournisseur + récapitulatif et opérations sans facture (CSV)</div></div>
      <Input type="month" value={month} max={new Date().toISOString().slice(0, 7)} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="invoice-export-month" />
      <Button size="sm" className="rounded-full" disabled={busy || !month} onClick={run} data-testid="invoice-export-btn">{busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />}Exporter le mois</Button>
    </div>
  );
};

export const InvoiceInbox = ({ company }) => {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("todo");
  const key = ["biz-invoices", company.id];
  const { data = [] } = useQuery({ queryKey: key, queryFn: () => api.get(`/business/companies/${company.id}/invoices`).then((r) => r.data),
    refetchInterval: (q) => ((q.state.data || []).some((i) => i.status === "processing") ? 3000 : false) });
  const refresh = () => { ["biz-invoices", "biz-suppliers", "biz-tx", "biz-overview", "biz-dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); };
  const rows = data.filter((i) => filter === "all" || (filter === "matched" ? i.match : !i.match));
  return (
    <div className="space-y-4" data-testid="invoice-inbox">
      {company.my_role !== "viewer" && <Dropzone cid={company.id} onDone={refresh} />}
      <Export cid={company.id} />
      <div className="flex flex-wrap gap-1.5">{FILTERS.map(([k, l]) => <button key={k} type="button" onClick={() => setFilter(k)} className={cn("rounded-full border px-3 py-1 text-xs", k === filter ? "border-primary bg-primary/10 font-bold" : "border-border")} data-testid={`invoice-filter-${k}`}>{l} ({data.filter((i) => k === "all" || (k === "matched" ? i.match : !i.match)).length})</button>)}</div>
      {rows.length ? <ul className="grid gap-3 lg:grid-cols-2">{rows.map((i) => <InvoiceRow key={i.id} inv={i} cid={company.id} company={company} onChange={refresh} />)}</ul>
        : <p className="card-surface p-6 text-sm text-muted-foreground" data-testid="invoice-empty">Aucune facture ici.</p>}
    </div>
  );
};
